import { LocaleCode, MarketCode } from '@tink/config/src/markets';
import { ProductType } from '@tink/core/src/types';
import { PISCapability } from '@tink/core/src/services/ProviderGroupsService/types';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ProviderEntity } from '../models/provider';
import { ResponseObj } from '../lib-api/services/base';
import { ProductDimension, ProductDimensionPay } from '../models/product';
import { ProvidersList } from '../lib-api/services/Providers';
import { ApplicationError } from '../components/ApplicationErrorBoundary/errors/ApplicationError';
import { CommonError } from '../stores/app-state-errors';
import { ProviderCapability } from '../models/providerCapability';
import { applicationParameters } from '../utilities/application-parameters';
import { ProvidersClient, providersClient } from './providersClient';

export type FetchProvidersPayload = {
  market: MarketCode;
  locale: LocaleCode;
  productType: ProductType;
  productDimension?: ProductDimension;
};

export const ERROR_PROVIDER: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_PROVIDER,
};

export const ERROR_PROVIDERS: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_PROVIDERS,
};

export type ProvidersService = {
  fetchProviders: (payload: FetchProvidersPayload) => Promise<ProviderEntity[]>;
  fetchProvider: (providerName: string) => Promise<ProviderEntity>;
};

export const createProvidersService = (client: ProvidersClient): ProvidersService => ({
  async fetchProviders({ market, locale, productType, productDimension }: FetchProvidersPayload) {
    const providerSpecifications = {
      market,
      locale,
    };

    let providersResponse: ResponseObj<ProvidersList>;

    if (productType === ProductType.PAY) {
      if (productDimension === ProductDimensionPay.BULK) {
        providersResponse = await client.list({
          ...providerSpecifications,
          queryParams: { pisCapability: PISCapability.PIS_BULK_PAYMENTS },
        });
      } else {
        providersResponse = await client.list({
          ...providerSpecifications,
          queryParams: { capability: ProviderCapability.TRANSFERS },
        });
      }
    } else {
      providersResponse = await client.list(providerSpecifications);
    }

    if (providersResponse.isOk()) {
      return providersResponse.data.providers;
    }
    throw new ApplicationError(ERROR_PROVIDERS);
  },

  async fetchProvider(providerName: string) {
    const locale = applicationParameters.get('locale');
    const response = await client.get({ providerName, locale });

    if (response.isOk()) {
      return response.data.providers[0];
    }

    throw new ApplicationError(ERROR_PROVIDER);
  },
});

export const providersService = createProvidersService(providersClient);
